// src/lib/progressRemote.js
// STATUS: active | UPDATED: 2026-08-02
// LOG:
//   2026-08-02  New: Supabase persistence layer for training progress. Detects
//               whether migration 0003 exists on the live project; if not, every
//               call is a silent no-op and the app stays local-first.
// NEXT: Manager roster queries (Phase 2) build on the same step id map.
//
// All writes are fire-and-forget: the UI never waits on the network. Reads
// happen once per login via pullRemote, merged into the local cache by
// progressStore.hydrateProgress. Auth is the user's own session, so RLS
// (self all, manager read) applies exactly as written in 0003.

import { supabase } from './supabase'

let enabled = null // null = unknown, false = tables missing / no session, true = live
let stepIdByNumber = null
let stepNumberById = null

// Cache SUCCESS forever, never cache failure.
//
// This used to memoize both: `if (enabled !== null) return enabled`. One early
// call, before the session resolved or during a blip, latched enabled=false for
// the rest of the page load, and every write after it was a silent no-op until
// a reload. The comment under this function has always claimed each write
// re-checks so persistence starts without a redeploy. It did not. It does now.
async function detect() {
  if (enabled === true) return true
  try {
    const { data, error } = await supabase.from('steps').select('id, number').limit(20)
    if (error || !data || data.length === 0) {
      enabled = false
      return enabled
    }
    stepIdByNumber = {}
    stepNumberById = {}
    for (const row of data) {
      stepIdByNumber[row.number] = row.id
      stepNumberById[row.id] = row.number
    }
    enabled = true
  } catch {
    enabled = false
  }
  return enabled
}

export async function remoteAvailable() {
  return detect()
}

// Pull the user's remote state and return it in the local progressStore shape,
// or null when remote is unavailable. Never throws.
export async function pullRemote(userId) {
  if (!userId || !(await detect())) return null
  try {
    const [prog, commits, activity] = await Promise.all([
      supabase
        .from('step_progress')
        .select('step_id, status, best_score, started_at, completed_at')
        .eq('user_id', userId),
      supabase
        .from('commitments')
        .select('id, step_id, body, status, created_at, resolved_at')
        .eq('user_id', userId),
      supabase
        .from('activity_events')
        .select('created_at')
        .eq('user_id', userId)
        .eq('kind', 'training_activity')
        .order('created_at', { ascending: false })
        .limit(400),
    ])
    if (prog.error) return null

    const steps = {}
    for (const row of prog.data || []) {
      const number = stepNumberById[row.step_id]
      if (!number) continue
      steps[number] = {
        status: row.status,
        bestScore: row.best_score != null ? Number(row.best_score) : null,
        startedAt: row.started_at,
        completedAt: row.completed_at,
      }
    }

    const commitments = (commits.data || []).map((c) => ({
      id: c.id,
      stepNumber: stepNumberById[c.step_id] || null,
      body: c.body,
      status: c.status,
      createdAt: c.created_at,
      resolvedAt: c.resolved_at,
    }))

    const activityDays = []
    for (const row of activity.data || []) {
      const key = String(row.created_at).slice(0, 10)
      if (!activityDays.includes(key)) activityDays.push(key)
    }

    return { steps, commitments, activityDays }
  } catch {
    return null
  }
}

// ---- Writes are fire and forget so the UI never waits on the network. Each
// one re-checks detect(), so persistence starts as soon as it can without a
// redeploy.
//
// FIRE AND FORGET IS NOT THE SAME AS UNCHECKED. supabase-js does not throw when
// RLS rejects a write, it resolves with an { error } that these calls used to
// drop on the floor. A salesperson's finished step could fail to save and
// nothing anywhere would say so: no console line, no toast, no retry. Silent
// data loss is the worst kind, because you only discover it when someone asks
// where their progress went.
//
// report() does not surface anything to the user on purpose. These writes are
// deliberately invisible when they succeed, and a training app should not
// interrupt somebody mid-lesson about a background upsert. It makes the failure
// findable, which is the difference between a bug you can chase and one you
// cannot. ----
function report(label, error) {
  if (error) console.error(`progressRemote: ${label} failed`, error.message || error)
}

export function pushStepProgress(userId, stepNumber, record) {
  void (async () => {
    if (!userId || !(await detect())) return
    const stepId = stepIdByNumber[stepNumber]
    if (!stepId) return
    const { error } = await supabase.from('step_progress').upsert(
      {
        user_id: userId,
        step_id: stepId,
        status: record.status || 'in_progress',
        best_score: record.bestScore ?? null,
        completed_at: record.completedAt || null,
      },
      { onConflict: 'user_id,step_id' }
    )
    report('pushStepProgress', error)
  })()
}

export function pushAttempt(userId, stepNumber, attempt) {
  void (async () => {
    if (!userId || !(await detect())) return
    const stepId = stepIdByNumber[stepNumber]
    if (!stepId) return
    const { error } = await supabase.from('knowledge_check_attempts').insert({
      user_id: userId,
      step_id: stepId,
      score: attempt.score,
      total: attempt.total,
      passed: attempt.passed,
      answers: attempt.answers || [],
    })
    report('pushAttempt', error)
  })()
}

export function pushCommitment(userId, commitment) {
  void (async () => {
    if (!userId || !(await detect())) return
    const stepId = stepIdByNumber[commitment.stepNumber]
    if (!stepId) return
    const { error } = await supabase.from('commitments').insert({
      id: commitment.id,
      user_id: userId,
      step_id: stepId,
      body: commitment.body,
      status: commitment.status,
    })
    report('pushCommitment', error)
  })()
}

export function pushCommitmentResolution(userId, commitmentId, status, resolvedAt) {
  void (async () => {
    if (!userId || !(await detect())) return
    const { error } = await supabase
      .from('commitments')
      .update({ status, resolved_at: resolvedAt })
      .eq('id', commitmentId)
      .eq('user_id', userId)
    report('pushCommitmentResolution', error)
  })()
}

export function pushActivity(userId) {
  void (async () => {
    if (!userId || !(await detect())) return
    const today = new Date().toISOString().slice(0, 10)
    const { data, error: readError } = await supabase
      .from('activity_events')
      .select('id')
      .eq('user_id', userId)
      .eq('kind', 'training_activity')
      .gte('created_at', `${today}T00:00:00Z`)
      .limit(1)
    if (readError) {
      // Cannot tell whether today is already recorded. Stop rather than risk a
      // duplicate: this row is what the streak counts.
      report('pushActivity read', readError)
      return
    }
    if (data && data.length > 0) return
    const { error } = await supabase.from('activity_events').insert({
      user_id: userId,
      kind: 'training_activity',
      metadata: { source: 'progressStore' },
    })
    report('pushActivity', error)
  })()
}
